import React from "react";
import "./RecoverWhereYouLeftOffAnimation.css";
import MindrailLogo from "./MindrailLogo";
import { Check, Clock, FileText, RotateCcw } from "lucide-react";

export default function RecoverWhereYouLeftOffAnimation() {
  return (
    <div className="absolute inset-0 mr-rec overflow-hidden">
      {/* Background ambient SVG */}
      <svg
        viewBox="0 0 400 340"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <g stroke="#2563EB" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.14">
          <path d="M 20 60 Q 60 40 100 60 T 180 58 T 260 50 T 340 60 T 390 56" />
          <path d="M 30 80 Q 80 65 130 78 T 230 70 T 330 82 T 390 75" opacity="0.6" />
        </g>
        <circle className="mr-rec-amb mr-rec-amb-1" cx="48" cy="180" r="1.8" fill="#2563EB" opacity="0" />
        <circle className="mr-rec-amb mr-rec-amb-2" cx="360" cy="160" r="2" fill="#2563EB" opacity="0" />
        <circle className="mr-rec-amb mr-rec-amb-3" cx="30" cy="260" r="1.5" fill="#2563EB" opacity="0" />
      </svg>

      {/* ===== FLOATING PILL: Session saved ===== */}
      <div className="mr-rec-pill-saved absolute top-[5%] right-[4%]" aria-hidden>
        <div className="bg-white border border-[#E2E8F0] rounded-full shadow-[0_8px_20px_rgba(15,23,42,0.06)] px-3 py-1.5 flex items-center gap-1.5">
          <span className="w-5 h-5 rounded-full bg-[#DBEAFE] flex items-center justify-center">
            <Clock className="w-2.5 h-2.5 text-[#2563EB]" strokeWidth={2.2} />
          </span>
          <span className="font-label text-[9px] font-bold text-[#0F172A] whitespace-nowrap">
            Session saved
          </span>
          <span className="text-[9px] font-medium text-[#475569]">9m ago</span>
        </div>
      </div>

      {/* ===== FLOATING PILL: Welcome back ===== */}
      <div className="mr-rec-pill-welcome absolute top-[5%] left-[4%]" aria-hidden>
        <div className="bg-white border border-[#E2E8F0] rounded-full shadow-[0_8px_20px_rgba(15,23,42,0.06)] px-3 py-1.5 flex items-center gap-1.5">
          <span className="w-5 h-5 rounded-full bg-[#DCFCE7] flex items-center justify-center">
            <RotateCcw className="w-2.5 h-2.5 text-[#22C55E]" strokeWidth={2.2} />
          </span>
          <span className="font-label text-[9px] font-bold text-[#0F172A] whitespace-nowrap">
            Welcome back
          </span>
        </div>
      </div>

      {/* ===== CENTER BROWSER MOCKUP ===== */}
      <div className="mr-rec-browser">
        <div className="mr-rec-chrome">
          <span className="mr-rec-dot mr-rec-dot-red" />
          <span className="mr-rec-dot mr-rec-dot-amber" />
          <span className="mr-rec-dot mr-rec-dot-green" />
          <div className="flex items-center gap-1 ml-1.5 bg-white border border-[#E2E8F0] rounded-t-md rounded-b-none px-1.5 py-1 text-[7px]">
            <FileText className="w-2 h-2 text-[#2563EB]" strokeWidth={2.2} />
            <span className="font-display font-semibold text-[#0F172A]">Q1 Strategy — Studio North</span>
          </div>
        </div>

        {/* Document content */}
        <div className="flex-1 p-3 flex gap-2.5 min-h-0">
          <div className="flex-1 min-w-0">
            <p className="font-display text-[11px] font-bold text-[#0F172A]">
              Q1 Strategy — Studio North
            </p>
            <p className="text-[7px] text-[#475569] mt-0.5">Last edit · Jordan · Today, 4:32 PM</p>
            <div className="mt-2.5 space-y-1.5">
              {[100, 75, 92, 56, 85, 68, 95, 62, 80, 72, 88, 58].map((w, i) => (
                <span
                  key={i}
                  className={`block h-1 rounded-full ${i === 3 ? "bg-[#DBEAFE]" : "bg-[#E2E8F0]"}`}
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
          </div>
          {/* sidebar skeleton */}
          <div className="w-[22%] space-y-1.5 shrink-0">
            <span className="block h-7 rounded-md bg-[#DCFCE7]" />
            <span className="block h-1 rounded-full bg-[#E2E8F0] w-3/4" />
            <span className="block h-7 rounded-md bg-[#FEF3C7]" />
            <span className="block h-1 rounded-full bg-[#E2E8F0]" />
            <span className="block h-1 rounded-full bg-[#E2E8F0] w-5/6" />
            <span className="block h-7 rounded-md bg-[#FFE4DE]" />
          </div>
        </div>

        {/* Away dim overlay */}
        <div className="mr-rec-dim">
          <div className="mr-rec-dim-clock" />
          <div className="mr-rec-dim-stamp">Session saved</div>
          <div className="mr-rec-dim-saved">9 min ago</div>
        </div>
      </div>

      {/* ===== RESUME CARD — floating bottom-center ===== */}
      <div className="mr-rec-card">
        <div className="mr-rec-card-head">
          <div className="mr-rec-card-logo">
            <img src="/mindrail-icon.png" alt="Mindrail" style={{ width: "70%", height: "70%" }} />
          </div>
          <span className="font-display text-[10px] font-bold text-[#0F172A]">Mindrail</span>
          <span className="ml-auto font-label text-[7px] font-bold text-[#2563EB] bg-[#DBEAFE] px-1.5 py-0.5 rounded-md tracking-[0.1em]">RESUME</span>
        </div>
        <p className="font-display text-[11px] font-bold text-[#0F172A] leading-tight">
          Pick up where you left off?
        </p>
        <div className="mr-rec-card-session">
          <div className="flex items-center gap-1.5 text-[8px] text-[#0F172A]">
            <span className="w-1.5 h-1.5 rounded-sm bg-[#2563EB]" />
            4 tabs · 1 doc
          </div>
          <p className="font-display text-[9px] font-semibold text-[#0F172A] mt-0.5">Q1 Strategy — Studio North</p>
          <p className="font-label text-[7px] text-[#475569]">Cursor at line 14 · 9 min ago</p>
        </div>
        <div className="mr-rec-card-actions">
          <span className="mr-rec-card-resume">Resume session</span>
          <span className="text-[8px] font-semibold text-[#475569] px-2 py-1.5">Maybe later</span>
        </div>
      </div>

      {/* ===== "Resume here" floating button ===== */}
      <div className="mr-rec-btn-resume absolute right-[5%] bottom-[18%]" aria-hidden>
        <div className="bg-[#2563EB] text-white rounded-full shadow-[0_6px_18px_rgba(37,99,235,0.30)] px-3.5 py-2 flex items-center gap-1.5">
          <RotateCcw className="w-3 h-3" strokeWidth={2.2} />
          <span className="font-display text-[9px] font-bold whitespace-nowrap">Resume here</span>
        </div>
      </div>

      {/* ===== SUCCESS PILL ===== */}
      <div className="mr-rec-pill-success absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" aria-hidden>
        <div className="bg-white border border-[#DCFCE7] rounded-full shadow-[0_8px_20px_rgba(34,197,94,0.18)] px-3 py-1.5 flex items-center gap-1.5">
          <Check className="w-3 h-3 text-[#22C55E]" strokeWidth={2.8} />
          <span className="font-display text-[9px] font-bold text-[#22C55E] whitespace-nowrap">Right where you left off</span>
        </div>
      </div>

      {/* ===== BOTTOM TIMELINE / CONTINUITY TRAIL ===== */}
      <div className="mr-rec-timeline absolute left-3 right-3 bottom-3" aria-hidden>
        <div className="relative px-1">
          <div className="absolute left-3 right-3 top-[7px] h-px bg-[#E2E8F0]" />
          <div className="mr-rec-tl-fill absolute left-3 top-[7px] h-[2px] rounded-full bg-[#2563EB]" />
          <div className="relative flex justify-between items-start">
            {["Active doc", "Session saved", "Welcome back", "Context restored"].map((label, i) => (
              <div key={i} className={`mr-rec-tl-node mr-rec-tl-node-${i} flex flex-col items-center gap-1`}>
                <span className="mr-rec-tl-dot w-3.5 h-3.5 rounded-full bg-white border-2 border-[#E2E8F0]" />
                <span className="font-display text-[7px] font-semibold text-[#475569] text-center leading-tight whitespace-nowrap">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== FOOTER BRAND ===== */}
      <div className="mr-rec-foot">
        <MindrailLogo size={13} />
      </div>
    </div>
  );
}
